import React, { useState, useEffect, useCallback, useRef, memo, useMemo } from 'react';
import { Form, Button, Modal, Toast, ProgressBar } from 'react-bootstrap';
import useGroceryListIngredientStatus from '../hooks/groceryListIngredient';
import { useAddPantryIngredients } from '../hooks/pantryIngredient';
import { useAuth } from '../context/AuthContext';
import '../index.css';
import { fetchWithCSRF } from '../utils/csrfUtils';
import { getIngredientImageUrl } from '../utils/imageUtils';
import { getTodayLocalDateString } from '../utils/dateUtils';

const IngredientName = memo(({ children, textKey, onClick, disabled, ...props }) => {
  const spanRef = useRef(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [hasEverOverflowed, setHasEverOverflowed] = useState(false);

  useEffect(() => {
    setHasEverOverflowed(false); // Reset on text change
  }, [textKey]);

  useEffect(() => {
    const el = spanRef.current;
    if (!el) return;

    const checkOverflow = () => {
      // Add a 2px buffer to prevent oscillation
      const currentlyOverflowing = el.scrollWidth > el.clientWidth + 2;
      setIsOverflowing(prev => {
        if (prev !== currentlyOverflowing) {
          return currentlyOverflowing;
        }
        return prev;
      });
      if (currentlyOverflowing) {
        setHasEverOverflowed(true);
      }
    };

    checkOverflow(); // Initial check

    // Set up ResizeObserver
    const resizeObserver = new window.ResizeObserver(checkOverflow);
    resizeObserver.observe(el);

    // Also listen for window resize (in case parent containers change)
    window.addEventListener('resize', checkOverflow);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', checkOverflow);
    };
  }, [children]);

  const handleClick = (e) => {
    if (!disabled && onClick) {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <span
      ref={spanRef}
      className="ms-2 text-truncate"
      onClick={handleClick}
      style={{
        textDecoration: props.lineThrough ? 'line-through' : 'none',
        maxWidth: '240px',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        fontSize: hasEverOverflowed ? '0.75em' : '1em', // Shrink only, reset on text change
        transition: 'font-size 0.2s',
        cursor: disabled ? 'default' : 'pointer',
        ...props.style,
      }}
    >
      {children}
    </span>
  );
});

const GroceryDrawer = ({ isOpen, onClose, groceryListId, onItemsAddedToPantry }) => {
  // Raw data from API
  const [rawIngredients, setRawIngredients] = useState([]);
  // Stable ordered view that preserves positions during interactions
  const [stableIngredients, setStableIngredients] = useState([]);
  const [groceryListDetails, setGroceryListDetails] = useState(null);
  const { setGroceryListIngredientIsInCartStatus, setMultipleGroceryListIngredientsPurchasedStatus } = useGroceryListIngredientStatus();
  const { addPantryIngredients, isAdding: isAddingBatch } = useAddPantryIngredients();
  const { pgUser, sessionToken } = useAuth();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingItems, setPendingItems] = useState([]);
  const [pendingQuantities, setPendingQuantities] = useState({});
  const [editingQtyId, setEditingQtyId] = useState(null);
  const loadedGroceryListId = useRef(null);
  const [errorAddingToPantry, setErrorAddingToPantry] = useState(false);
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [showToast, setShowToast] = useState(false); //success toast
  const [toastMessage, setToastMessage] = useState('Items Added to Pantry');
  const isAddingToPantry = isAddingBatch;
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Handle responsive behavior
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Helper function to format quantity as clean fractions when possible
  const formatQuantity = useCallback((num) => {
    const allowedFractions = [
      { fraction: 1/16, label: "1/16", vulgar: "¹⁄₁₆" },
      { fraction: 1/8, label: "1/8", vulgar: "⅛" },
      { fraction: 1/6, label: "1/6", vulgar: "⅙" },
      { fraction: 1/4, label: "1/4", vulgar: "¼" },
      { fraction: 1/3, label: "1/3", vulgar: "⅓" },
      { fraction: 1/2, label: "1/2", vulgar: "½" },
      { fraction: 5/8, label: "5/8", vulgar: "⅝" },
      { fraction: 2/3, label: "2/3", vulgar: "⅔" },
      { fraction: 3/4, label: "3/4", vulgar: "¾" },
      { fraction: 5/6, label: "5/6", vulgar: "⅚" },
    ];

    const parsed = parseFloat(num);
    if (isNaN(parsed)) return num;

    const fractionTolerance = 0.008;
    const wholeTolerance = 0.02;
    const whole = Math.floor(parsed);
    const decimal = parsed - whole;

    // Check if it's basically a whole number
    if (decimal < wholeTolerance || decimal > (1 - wholeTolerance)) {
      return `${decimal > (1 - wholeTolerance) ? whole + 1 : whole}`;
    }

    // Check for fraction match
    const match = allowedFractions.find(f => Math.abs(decimal - f.fraction) < fractionTolerance);

    if (match) {
      const fractionSpan = <span style={{ fontSize: '1.3em' }}>{match.vulgar}</span>;
      if (whole > 0) {
        return <>{whole} {fractionSpan}</>;
      }
      return fractionSpan;
    }

    // Fall back to decimal, cleaned up
    const rounded = parsed.toFixed(3);
    return rounded.replace(/\.00$/, '');
  }, []);

  // Initial sorting function
  const sortIngredients = useCallback((ingredients) => {
    // Group by food group first
    const grouped = ingredients.reduce((acc, ingredient) => {
      const group = ingredient.primary_food_group || 'Other';
      if (!acc[group]) acc[group] = [];
      acc[group].push({...ingredient});
      return acc;
    }, {});
    
    // Sort within each group
    Object.keys(grouped).forEach(group => {
      grouped[group].sort((a, b) => {
        // First sort by purchased status (false comes before true)
        if (a.is_purchased !== b.is_purchased) {
          return a.is_purchased ? 1 : -1;
        }
        // Then sort by in-cart status (false comes before true)
        return Number(a.is_in_cart) - Number(b.is_in_cart);
      });
    });
    
    // Flatten grouped and sorted ingredients
    const sortedGroups = Object.keys(grouped).sort();
    const sortedIngredients = [];
    sortedGroups.forEach(group => {
      // Add group marker
      sortedIngredients.push({ isGroupHeader: true, groupName: group });
      // Add ingredients in this group
      sortedIngredients.push(...grouped[group]);
    });
    
    return sortedIngredients;
  }, []);

  const fetchIngredients = useCallback(async (listId) => {
    const idToUse = listId || groceryListId;
    if (!idToUse) return;
    
    try {
      if (!sessionToken) {
        throw new Error('No valid session found');
      }
      const response = await fetchWithCSRF(`/api/v1/grocery-list/${idToUse}/app_user/${pgUser.id}`, {
        headers: { 'Authorization': `Bearer ${sessionToken}` }
      });
      const responseData = await response.json();
      
      // Extract the grocery list details and ingredients from the nested structure
      const groceryList = responseData[0]?.data?.grocery_list;
      const groceryListIngredients = responseData[0]?.data?.grocery_list_ingredients;
      
      setGroceryListDetails(groceryList);
      
      const mappedIngredients = groceryListIngredients?.map(ingredient => ({
        ...ingredient,
        is_in_cart: ingredient.is_in_cart || false
      })) || [];
      
      // Always update the loaded id reference when fetch completes successfully
      loadedGroceryListId.current = idToUse;
      
      // Set raw ingredients data
      setRawIngredients(mappedIngredients);
      
      // Sort and create stable view of ingredients
      setStableIngredients(sortIngredients(mappedIngredients));
    } catch (error) {
      console.error('Error fetching grocery list ingredients:', error);
    }
  }, [pgUser?.id, groceryListId, sessionToken, sortIngredients]);

  useEffect(() => {
    if (groceryListId && isOpen && loadedGroceryListId.current !== groceryListId) {
      fetchIngredients(groceryListId);
    }
  }, [fetchIngredients, groceryListId, isOpen]);

  const handleIsInCart = useCallback(async (ingredientId, isInCart) => {
    // Optimistically update UI first for immediate feedback
    setRawIngredients(prev => prev.map(ingredient => 
      ingredient.id === ingredientId
      ? { ...ingredient, is_in_cart: isInCart }
      : ingredient
    ));
    
    setStableIngredients(prev => prev.map(item => 
      item.id === ingredientId
      ? { ...item, is_in_cart: isInCart }
      : item
    ));
    
    // Then make the API request
    try {
      await setGroceryListIngredientIsInCartStatus(ingredientId, isInCart, pgUser.id);
    } catch (error) {
      console.error('Error updating ingredient cart status:', error);
    }
  }, [setGroceryListIngredientIsInCartStatus, pgUser?.id]);

  // Direct add to pantry without modal confirmation
  const handleConfirmAddToPantryDirect = useCallback(async (itemsToAdd, quantities) => {
    if (itemsToAdd.length === 0) return;

    try {
      // Create array of pantry ingredients to add in batch
      const pantryIngredients = itemsToAdd.map(item => ({
        app_user_id: pgUser.id,
        pantry_id: pgUser.pantry_id,
        ingredient_id: item.ingredient_id,
        unit_of_measurement: item.unit_of_measurement,
        quantity: quantities[item.id],
        purchase_date: getTodayLocalDateString()
      }));

      // Add all items to pantry in a single API call
      // Pass true for isConfirmed since user is explicitly adding these items
      const result = await addPantryIngredients(pantryIngredients, true);
      
      if (result) {
        try {
          // Get array of IDs to mark as purchased
          const itemIds = itemsToAdd.map(item => item.id);
          
          // Mark all items as purchased in a single API call
          await setMultipleGroceryListIngredientsPurchasedStatus(itemIds, true, pgUser.id);
          
          // Update raw data
          setRawIngredients(prev => prev.map(ingredient => 
            itemIds.includes(ingredient.id) 
              ? { ...ingredient, is_purchased: true }
              : ingredient
          ));
          
          // Update stable view WITHOUT reordering
          setStableIngredients(prev => prev.map(item => 
            !item.isGroupHeader && itemIds.includes(item.id) 
              ? { ...item, is_purchased: true }
              : item
          ));

          // Show success toast
          setToastMessage('Items Added to Pantry');
          setShowToast(true);
          setTimeout(() => setShowToast(false), 3000);

          // Notify parent component to refresh pantry data
          if (onItemsAddedToPantry) {
            onItemsAddedToPantry();
          }
        } catch (error) {
          console.error('Error marking items as purchased:', error);
          setErrorAddingToPantry(true);
        }
      }
    } catch (error) {
      console.error('Error processing batch add to pantry:', error);
      setErrorAddingToPantry(true);
    }
  }, [addPantryIngredients, pgUser, setMultipleGroceryListIngredientsPurchasedStatus, onItemsAddedToPantry]);

  const handleDoneShopping = useCallback(() => {
    const checkedItems = rawIngredients.filter(ingredient => 
      ingredient.is_in_cart && !ingredient.is_purchased
    );
    if (checkedItems.length === 0) return;
    
    // Skip modal - directly prepare items and add to pantry
    setPendingItems(checkedItems);
    setPendingQuantities(
      Object.fromEntries(checkedItems.map(item => [item.id, item.quantity]))
    );
    
    // Directly call the add to pantry logic
    // We pass the items and quantities directly (not relying on state update)
    handleConfirmAddToPantryDirect(checkedItems, Object.fromEntries(checkedItems.map(item => [item.id, item.quantity])));
  }, [rawIngredients, handleConfirmAddToPantryDirect]);

  // COMMENTED OUT: Original modal-based confirmation flow
  /*
  const handleConfirmAddToPantry = useCallback(async () => {
    if (pendingItems.length === 0) return;

    try {
      // Create array of pantry ingredients to add in batch
      const pantryIngredients = pendingItems.map(item => ({
        app_user_id: pgUser.id,
        pantry_id: pgUser.pantry_id,
        ingredient_id: item.ingredient_id,
        unit_of_measurement: item.unit_of_measurement,
        quantity: pendingQuantities[item.id],
        purchase_date: getTodayLocalDateString()
      }));

      // Add all items to pantry in a single API call
      // Pass true for isConfirmed since user is explicitly adding these items
      const result = await addPantryIngredients(pantryIngredients, true);
      
      if (result) {
        try {
          // Get array of IDs to mark as purchased
          const itemIds = pendingItems.map(item => item.id);
          
          // Mark all items as purchased in a single API call
          await setMultipleGroceryListIngredientsPurchasedStatus(itemIds, true, pgUser.id);
          
          // Update raw data
          setRawIngredients(prev => prev.map(ingredient => 
            itemIds.includes(ingredient.id) 
              ? { ...ingredient, is_purchased: true }
              : ingredient
          ));
          
          // Update stable view WITHOUT reordering
          setStableIngredients(prev => prev.map(item => 
            !item.isGroupHeader && itemIds.includes(item.id) 
              ? { ...item, is_purchased: true }
              : item
          ));

          // Show success toast
          setToastMessage('Items Added to Pantry');
          setShowToast(true);
          setTimeout(() => setShowToast(false), 3000);

          // Notify parent component to refresh pantry data
          if (onItemsAddedToPantry) {
            onItemsAddedToPantry();
          }
        } catch (error) {
          console.error('Error marking items as purchased:', error);
          setErrorAddingToPantry(true);
        }
      }
    } catch (error) {
      console.error('Error processing batch add to pantry:', error);
      setErrorAddingToPantry(true);
    } finally {
      setShowConfirmModal(false);
    }
  }, [pendingItems, pendingQuantities, addPantryIngredients, pgUser, setMultipleGroceryListIngredientsPurchasedStatus, onItemsAddedToPantry]);
  */

  // COMMENTED OUT: These handlers were only used by the confirmation modal
  /*
  const roundToNearestQuarter = useCallback((value) => Math.round(value * 4) / 4, []);

  const handleQuantityChange = useCallback((id, delta) => {
    setPendingQuantities(prev => {
      const raw = (parseFloat(prev[id]) || 0.25) + delta;
      const newQty = Math.max(0.25, roundToNearestQuarter(raw));
      return { ...prev, [id]: parseFloat(newQty.toFixed(3)) };
    });
  }, [roundToNearestQuarter]);

  const handleQuantityInputChange = useCallback((id, value) => {
    if (value === '' || value === '.') {
      setPendingQuantities(prev => ({ ...prev, [id]: value }));
      return;
    }
    
    let num = parseFloat(value);
    if (isNaN(num)) {
      setPendingQuantities(prev => ({ ...prev, [id]: value }));
      return;
    }
    
    setPendingQuantities(prev => ({ ...prev, [id]: num }));
  }, []);

  const handleQuantityInputBlur = useCallback((id) => {
    setPendingQuantities(prev => {
      const currentValue = prev[id];
      let num = parseFloat(currentValue);
      
      if (isNaN(num) || num < 0.25) {
        num = 0.25;
      }
      
      return { ...prev, [id]: num };
    });
    setEditingQtyId(null);
  }, []);

  const handleQuantityInputKeyDown = useCallback((e, id) => {
    if (e.key === 'Enter') {
      setPendingQuantities(prev => {
        const currentValue = prev[id];
        let num = parseFloat(currentValue);
        
        if (isNaN(num) || num < 0.25) {
          num = 0.25;
        }
        
        return { ...prev, [id]: num };
      });
      setEditingQtyId(null);
    }
  }, []);
  */

  // Calculate progress stats
  const progressStats = useMemo(() => {
    const totalItems = rawIngredients.length;
    const checkedItems = rawIngredients.filter(item => item.is_in_cart || item.is_purchased).length;
    const percentage = totalItems > 0 ? Math.round((checkedItems / totalItems) * 100) : 0;
    
    return { totalItems, checkedItems, percentage };
  }, [rawIngredients]);

  // COMMENTED OUT: Memoized Modal component
  /*
  const ConfirmModal = useMemo(() => (
    <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)}>
      <Modal.Header closeButton>
        <Modal.Title>Confirm Items to Add to Pantry</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {pendingItems.length === 0 ? (
          <div>No items selected.</div>
        ) : (
          <ul className="list-unstyled">
            {pendingItems.map(item => (
              <li key={item.id} className="mb-3 d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center gap-2">
                  <img
                    src={getIngredientImageUrl(item.ingredient_id)}
                    alt={item.ingredient_name}
                    style={{ width: '32px', height: '32px', objectFit: 'cover', borderRadius: '4px', marginRight: '0.5rem', background: '#ffffff' }}
                    onError={e => { e.target.onerror = null; e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIiIGhlaWdodD0iMzIiIHZpZXdCb3g9IjAgMCAzMiAzMiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMzIiIGhlaWdodD0iMzIiIGZpbGw9IiNFMEUwRTAiLz48cGF0aCBkPSJNMTIgMTZIMTZWMTlIMjFDMjIuMTA0IDE5IDIyLjg5NiAxOC4yMDggMjIuODk2IDE3VjE1QzIyLjg5NiAxMy43OTIgMjIuMTA0IDEzIDIxIDEzSDE2VjE2SDEyWiIgZmlsbD0iIzkwOTA5MCIvPjwvc3ZnPg=='; }}
                  />
                  <span>
                    {item.ingredient_name} ({item.unit_of_measurement})
                  </span>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <Button 
                    variant="outline-secondary" 
                    size="sm" 
                    onClick={() => handleQuantityChange(item.id, -0.25)}
                    style={{ minWidth: '2rem', fontWeight: 'bold' }}
                  >
                    −
                  </Button>
                  {editingQtyId === item.id ? (
                    <input
                      type="number"
                      step="0.25"
                      min="0.25"
                      value={pendingQuantities[item.id]}
                      autoFocus
                      style={{ width: '3.5rem', textAlign: 'center', borderRadius: '4px', border: '1px solid #ccc' }}
                      onChange={e => handleQuantityInputChange(item.id, e.target.value)}
                      onBlur={() => handleQuantityInputBlur(item.id)}
                      onKeyDown={e => handleQuantityInputKeyDown(e, item.id)}
                    />
                  ) : (
                    <span
                      style={{ minWidth: '2.5rem', textAlign: 'center', cursor: 'pointer', borderBottom: '1px dashed #888' }}
                      onClick={() => setEditingQtyId(item.id)}
                      title="Click to edit"
                    >
                      {formatQuantity(pendingQuantities[item.id])}
                    </span>
                  )}
                  <Button 
                    variant="outline-secondary" 
                    size="sm" 
                    onClick={() => handleQuantityChange(item.id, 0.25)}
                    style={{ minWidth: '2rem', fontWeight: 'bold' }}
                  >
                    +
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => setShowConfirmModal(false)} disabled={isAddingToPantry}>
          Cancel
        </Button>
        <Button 
          variant="primary" 
          onClick={handleConfirmAddToPantry}
          disabled={isAddingToPantry || pendingItems.length === 0}
          style={{ backgroundColor: '#52cd92', borderColor: '#52cd92' }}
        >
          {isAddingToPantry ? 'Adding Items to Pantry...' : 'Confirm & Add to Pantry'}
        </Button>
      </Modal.Footer>
    </Modal>
  ), [showConfirmModal, pendingItems, pendingQuantities, editingQtyId, handleQuantityChange, handleQuantityInputChange, handleQuantityInputBlur, handleQuantityInputKeyDown, handleConfirmAddToPantry, isAddingToPantry, formatQuantity]);
  */

  return (
    <>
      {/* Overlay */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 1040,
          opacity: isOpen ? 1 : 0,
          visibility: isOpen ? 'visible' : 'hidden',
          transition: 'opacity 0.3s ease-in-out, visibility 0.3s ease-in-out'
        }}
      />

      {/* Drawer */}
      <div
        style={{
          position: 'fixed',
          ...(isMobile ? {
            left: 0,
            right: 0,
            bottom: 0,
            top: 'auto',
            height: '85vh',
            width: '100%',
            maxWidth: '100%',
            transform: isOpen ? 'translateY(0)' : 'translateY(100%)',
          } : {
            top: 0,
            right: 0,
            bottom: 0,
            left: 'auto',
            width: '95%',
            maxWidth: '500px',
            transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
          }),
          backgroundColor: 'white',
          boxShadow: isMobile ? '0 -2px 8px rgba(0, 0, 0, 0.15)' : '-2px 0 8px rgba(0, 0, 0, 0.15)',
          zIndex: 1050,
          transition: 'transform 0.3s ease-in-out',
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* Header */}
        <div style={{
          padding: '1.5rem',
          borderBottom: '2px solid #dee2e6',
          position: 'sticky',
          top: 0,
          backgroundColor: 'white',
          zIndex: 10
        }}>
          <div className="d-flex align-items-center mb-3">
            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '2rem',
                cursor: 'pointer',
                padding: '0',
                width: '40px',
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                color: '#666',
                lineHeight: 1,
                marginRight: '1rem'
              }}
              aria-label="Close"
            >
              ×
            </button>
            <h4 className="mb-0" style={{ fontWeight: 'bold' }}>Grocery List</h4>
          </div>

          {/* Tab */}
          {/* <div 
            className="d-flex mb-3" 
            style={{
              backgroundColor: '#52cd92',
              borderRadius: '6px',
              padding: '3px',
              gap: '0',
              width: 'fit-content'
            }}
          >
            <button
              style={{
                padding: '0.35rem 0.75rem',
                border: 'none',
                borderRadius: '4px',
                backgroundColor: 'white',
                color: '#333',
                fontWeight: '600',
                cursor: 'pointer',
                fontSize: '0.85rem'
              }}
            >
              This Week
            </button>
            <button
              style={{
                padding: '0.35rem 0.75rem',
                border: 'none',
                borderRadius: '4px',
                backgroundColor: 'transparent',
                color: 'white',
                fontWeight: '600',
                cursor: 'pointer',
                fontSize: '0.85rem'
              }}
            >
              Next Week   
            </button>
          </div> */}

          {/* Progress Bar */}
          {progressStats.totalItems > 0 && (
            <div className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <small style={{ fontWeight: '600', color: '#333' }}>
                  {progressStats.checkedItems} of {progressStats.totalItems} items in cart
                </small>
                <small style={{ fontWeight: '600', color: '#52cd92' }}>
                  {progressStats.percentage}%
                </small>
              </div>
              <ProgressBar 
                now={progressStats.percentage} 
                style={{ height: '8px' }}
                variant="success"
              />
            </div>
          )}

          {errorAddingToPantry && (
            <div className="alert alert-danger mt-2 px-2 py-2" role="alert">
              Error adding items to pantry. Please try again.
            </div>
          )}

          <small className="text-muted d-block mt-2">
            💡 Check items you've purchased, then click Complete Shopping
          </small>
        </div>

        {/* Content */}
        <div style={{ flex: 1, padding: '1rem', paddingBottom: '0' }}>
          {/* <div className="d-flex justify-content-between align-items-center px-3 mb-2" style={{ fontWeight: 600, color: '#555', fontSize: '0.9rem' }}>
            <span>Got It</span>
            <span>Item</span>
            <span>Quantity</span>
          </div> */}
          {/* <hr style={{ borderTop: '2px solid black', margin: '0 0 0.5rem 0' }} /> */}

          <ul className="list-unstyled">
            {(() => {
              // Show loading state if we have a groceryListId but no ingredients yet
              if (groceryListId && stableIngredients.length === 0) {
                if (loadedGroceryListId.current !== groceryListId) {
                  return (
                    <div className="text-center py-4">
                      <div className="spinner-border" role="status" style={{ color: '#52cd92' }}>
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <p className="mt-2">Loading grocery list...</p>
                    </div>
                  );
                } else {
                  return (
                    <div className="text-center py-4">
                      <p className="mt-2">No items on your list!</p>
                    </div>
                  );
                }
              }
              
              return stableIngredients.map((item, index) => {
                if (item.isGroupHeader) {
                  // Render group header
                  return (
                    <li key={`group-${item.groupName}`}>
                      <h6 style={{ 
                        marginTop: '1rem', 
                        marginBottom: '0.5rem', 
                        color: 'black', 
                        fontWeight: 700,
                        fontSize: '1rem',
                        textAlign: 'left',
                        paddingLeft: '10px'
                      }}>
                        {item.groupName}
                      </h6>
                    </li>
                  );
                } else {
                  // Render ingredient item
                  return (
                    <li 
                      key={item.id} 
                      className={`d-flex align-items-center py-2 px-1 border-bottom ${item.is_purchased ? 'text-muted' : ''}`}
                      style={{ 
                        opacity: item.is_purchased ? '0.6' : '1'
                      }}
                    >
                      <div style={{ width: '15%', textAlign: 'center' }}>
                        <Form.Check
                          type="checkbox"
                          checked={item.is_purchased || item.is_in_cart}
                          onChange={(e) => handleIsInCart(item.id, e.target.checked)}
                          className="custom-checkbox"
                          disabled={item.is_purchased}
                          style={{ 
                            cursor: item.is_purchased ? 'not-allowed' : 'pointer'
                          }}
                        />
                      </div>

                      <div className="d-flex align-items-center" style={{ flex: 1, minWidth: 0 }}>
                        <img
                          src={getIngredientImageUrl(item.ingredient_id)}
                          alt={item.ingredient_name}
                          style={{ width: '28px', height: '28px', objectFit: 'cover', borderRadius: '4px', marginRight: '0.5rem', background: '#ffffff' }}
                          onError={e => { e.target.onerror = null; e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIiIGhlaWdodD0iMzIiIHZpZXdCb3g9IjAgMCAzMiAzMiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMzIiIGhlaWdodD0iMzIiIGZpbGw9IiNFMEUwRTAiLz48cGF0aCBkPSJNMTIgMTZIMTZWMTlIMjFDMjIuMTA0IDE5IDIyLjg5NiAxOC4yMDggMjIuODk2IDE3VjE1QzIyLjg5NiAxMy43OTIgMjIuMTA0IDEzIDIxIDEzSDE2VjE2SDEyWiIgZmlsbD0iIzkwOTA5MCIvPjwvc3ZnPg=='; }}
                        />
                        <span
                          onClick={() => {
                            if (!item.is_purchased) {
                              const newIsInCartValue = !(item.is_purchased || item.is_in_cart);
                              handleIsInCart(item.id, newIsInCartValue);
                            }
                          }}
                          style={{
                            textDecoration: (item.is_purchased || item.is_in_cart) ? 'line-through' : 'none',
                            fontSize: '0.85rem',
                            cursor: item.is_purchased ? 'default' : 'pointer',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {item.ingredient_name}
                          {item.notes && <small className="text-muted ms-1">({item.notes})</small>}
                        </span>
                      </div>

                      <div 
                        style={{ 
                          minWidth: '80px', 
                          textAlign: 'right', 
                          fontWeight: 500,
                          fontSize: '0.9rem'
                        }}
                      >
                        {formatQuantity(item.quantity)} {item.unit_of_measurement}
                      </div>
                    </li>
                  );
                }
              });
            })()}
          </ul>

          {groceryListDetails && (
            <div className="mt-3 text-center small text-muted">
              {groceryListDetails.total_items} Items | 
              Estimated Cost: ${(groceryListDetails.estimated_total_cost_in_cents / 100).toFixed(2)}
            </div>
          )}

          {/* Spacer for sticky footer */}
          <div style={{ height: '80px' }} />
        </div>

        {/* Sticky Footer with Complete Shopping Button */}
        <div style={{
          position: 'sticky',
          bottom: 0,
          left: 0,
          right: 0,
          padding: '1rem',
          backgroundColor: 'white',
          borderTop: '1px solid #e0e0e0',
          boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.1)',
          zIndex: 1000
        }}>
          <Button 
            onClick={handleDoneShopping}
            disabled={isAddingToPantry || !rawIngredients.some(i => i.is_in_cart && !i.is_purchased)}
            className="w-100"
            style={{
              backgroundColor: '#52cd92',
              borderColor: '#52cd92',
              fontWeight: '600',
              padding: '0.75rem 1rem',
              fontSize: '1rem',
              borderRadius: '8px',
              boxShadow: '0 2px 8px rgba(82, 205, 146, 0.3)'
            }}
          >
            Complete Shopping
          </Button>
        </div>
      </div>

      {/* Modals and Toasts */}
      {/* COMMENTED OUT: Modal is no longer used */}
      {/* {ConfirmModal} */}

      {/* Error Toast */}
      <Toast
        show={showErrorToast}
        onClose={() => setShowErrorToast(false)}
        style={{
          position: 'fixed',
          top: 20,
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: '#f8d7da',
          borderColor: '#f5c2c7',
          color: '#842029',
          zIndex: 2000,
          minWidth: '250px'
        }}
      >
        <Toast.Header>
          <strong className="me-auto">Error</strong>
        </Toast.Header>
        <Toast.Body>
          An error occurred. Please refresh the page and try again.
        </Toast.Body>
      </Toast>

      {/* Success Toast */}
      <Toast 
        show={showToast} 
        onClose={() => setShowToast(false)}
        style={{
          position: 'fixed',
          top: '10px',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 9999,
          backgroundColor: '#52cd92',
          border: 'none',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          minWidth: '300px'
        }}
      >
        <Toast.Header style={{ 
          backgroundColor: '#52cd92', 
          borderBottom: '1px solid rgba(255,255,255,0.2)',
          display: 'flex',
          justifyContent: 'center',
          position: 'relative',
          width: '100%'
        }}>
          <strong style={{ color: 'white' }}>{toastMessage}</strong>
        </Toast.Header>
      </Toast>
    </>
  );
};

export default GroceryDrawer;

